/**
 * French catalogue.
 *
 * Mirrors `en.ts` exactly — a test fails if the shapes drift.
 *
 * Terminology follows INPI/EUIPO French usage rather than literal translation:
 * "opposition" is the same word but a legal term of art, "marque" (not
 * "marque commerciale") is what a French practitioner says for trademark, and
 * "titulaire" is the owner of a registered mark. Getting these wrong is more
 * damaging than leaving the UI in English, because they read as competent while
 * being subtly off to the only people who will use this.
 */
import monitoring from './fr/monitoring'
import trademarks from './fr/trademarks'
import general from './fr/general'
import people from './fr/people'
import explain from './fr/explain'

export default {
  nav: {
    workspace: 'Espace de travail',
    workspaceTools: 'Outils',
    dashboard: 'Tableau de bord',
    monitoring: 'Surveillance',
    trademarks: 'Mes marques',
    oppositions: 'Oppositions',
    reports: 'Rapports',
    clients: 'Clients',
    settings: 'Paramètres',
  },

  topbar: {
    openMenu: 'Ouvrir le menu',
    closeMenu: 'Fermer le menu',
    search: 'Rechercher marques, titulaires, classes…',
    searchLabel: 'Rechercher',
    openSearch: 'Ouvrir la recherche globale',
    notifications: 'Notifications',
    notificationsUnread: 'Notifications, {n} non lues',
    help: 'Aide',
    account: 'Compte',
  },

  account: {
    profile: 'Profil',
    settings: 'Paramètres',
    security: 'Sécurité',
    help: "Centre d'aide",
    signOut: 'Se déconnecter',
    language: 'Langue',
    menuLabel: 'Compte',
    page: {
      title: 'Votre compte',
      subtitle: 'Votre nom, votre langue et votre mot de passe.',
      loadFailed: "Votre compte n'a pas pu être chargé.",
      profile: 'Profil personnel',
      profileSub: 'Ce que voient vos collègues, et votre nom dans l’historique des dossiers.',
      name: 'Nom complet',
      email: 'E-mail de connexion',
      emailHint: 'Votre adresse de connexion est modifiée par le cabinet, pas ici.',
      tier: 'Type de compte',
      firm: 'Cabinet',
      tiers: {
        firm_main: 'Compte principal du cabinet',
        firm_manager: 'Gestionnaire',
        client_viewer: 'Compte client (lecture seule)',
      },
      save: 'Enregistrer le profil',
      saved: 'Profil enregistré',
      failed: "Le profil n'a pas été enregistré",
      nameRequired: 'Saisissez votre nom.',
      password: 'Mot de passe',
      passwordSub: 'Pour le changer, votre mot de passe actuel est demandé d’abord.',
      current: 'Mot de passe actuel',
      new: 'Nouveau mot de passe',
      repeat: 'Répétez le nouveau mot de passe',
      minLength: 'Au moins {n} caractères.',
      tooShort: 'Le nouveau mot de passe doit compter au moins {n} caractères.',
      needCurrent: 'Saisissez votre mot de passe actuel.',
      mismatch: 'Les deux nouveaux mots de passe ne sont pas identiques.',
      change: 'Changer le mot de passe',
      changed: 'Mot de passe changé',
      wrongCurrent: "Votre mot de passe actuel n'est pas correct.",
      changeFailed: "Le mot de passe n'a pas été changé",
      notYet:
        "La connexion à deux facteurs, la liste des appareils connectés et les jetons d'API ne sont pas encore dans l'application.",
    },
  },

  auth: {
    title: 'Bon retour',
    subtitle: 'Connectez-vous pour gérer votre portefeuille de marques.',
    email: 'E-mail professionnel',
    // `{'@'}` is vue-i18n's literal escape: a bare @ is parsed as a link.
    emailPlaceholder: "vous{'@'}cabinet.com",
    password: 'Mot de passe',
    remember: 'Se souvenir de moi',
    forgot: 'Mot de passe oublié ?',
    signIn: 'Se connecter',
    signingIn: 'Connexion…',
    noAccount: "Vous n'avez pas de compte ?",
    requestAccess: 'Demander un accès',
    welcomeBack: 'Bon retour',
    failed: 'Échec de la connexion',
    checkCredentials: 'Vérifiez vos identifiants et réessayez.',
    enterEmail: 'Saisissez votre e-mail',
    enterPassword: 'Saisissez votre mot de passe',
    reset: {
      title: 'Réinitialiser votre mot de passe',
      sub: "Saisissez l'e-mail avec lequel vous vous connectez. S'il a un compte, nous lui envoyons un lien pour choisir un nouveau mot de passe.",
      submit: 'Envoyer le lien',
      sending: 'Envoi…',
      sent: 'Si cette adresse a un compte, un lien est en route.',
      sentSub:
        "Le lien fonctionne une fois, pendant 30 minutes. Si rien n'arrive, vérifiez les courriers indésirables ou redemandez-le.",
      back: 'Retour à la connexion',
      failed: "La demande n'a pas pu être envoyée. Réessayez dans un instant.",
    },
    link: {
      checking: 'Vérification du lien…',
      invalid: "Ce lien n'est plus valide",
      invalidSub: 'Un lien ne fonctionne qu’une fois et expire. Demandez-en un nouveau ci-dessous.',
      askNew: 'Demander un nouveau lien',
      unreachable: "Le lien n'a pas pu être vérifié",
      resetTitle: 'Nouveau mot de passe pour {email}',
      inviteTitle: 'Choisissez votre mot de passe pour {email}',
      resetSub: "Le nouveau mot de passe remplace l'ancien, et vous êtes connecté aussitôt.",
      inviteSub:
        'Vous avez été invité sur Lexentiqa. Choisissez un mot de passe et vous êtes connecté aussitôt.',
      expires: 'Ce lien expire le {when}.',
      password: 'Nouveau mot de passe',
      confirm: 'Répétez le mot de passe',
      minLength: 'Au moins {n} caractères.',
      tooShort: 'Le mot de passe doit compter au moins {n} caractères.',
      mismatch: 'Les deux mots de passe ne sont pas identiques.',
      submit: 'Enregistrer et se connecter',
      submitting: 'Enregistrement…',
      done: 'Mot de passe enregistré — vous êtes connecté',
      failed: "Le mot de passe n'a pas été enregistré",
    },
  },

  common: {
    save: 'Enregistrer',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    delete: 'Supprimer',
    edit: 'Modifier',
    close: 'Fermer',
    retry: 'Réessayer',
    loading: 'Chargement…',
    search: 'Rechercher',
    clearAll: 'Tout effacer',
    skipToContent: 'Aller au contenu principal',
  },

  states: {
    emptyTitle: 'Rien pour le moment',
    emptyMessage: 'Aucun résultat ne correspond aux filtres actuels.',
    errorTitle: "Une erreur s'est produite",
    errorMessage: "La page n'a pas pu être chargée.",
    notFoundTitle: 'Page introuvable',
    offline: 'Vous semblez être hors ligne.',
  },

  time: {
    dueToday: "À traiter aujourd'hui",
    daysLeft: '{n} jour restant | {n} jours restants',
    daysOverdue: '{n} jour de retard | {n} jours de retard',
  },

  /* La cloche et la page des notifications (W11). */
  notif: {
    subtitle:
      'Ce qui s’est passé dans vos dossiers et dans la surveillance, du plus récent au plus ancien. Chacune ouvre ce dont elle parle.',
    popupNew: '{n} nouvelles',
    popupEmpty: 'Rien pour le moment.',
    viewAll: 'Toutes les notifications',
    markAllRead: 'Tout marquer comme lu',
    markAllTitle: 'Marquer toutes les notifications comme lues ?',
    markAllBody: 'Cela vaut pour toutes, pas seulement celles de cette page.',
    markAllDone: 'Tout est marqué comme lu',
    settingsLink: 'Réglages des notifications',
    failed: "Cette modification n'a pas été enregistrée",
    loadFailed: "Les notifications n'ont pas pu être chargées.",
    count: '{n} notification | {n} notifications',
    unreadCount: '{n} non lues',
    unread: 'non lue',
    markRead: 'Marquer comme lue',
    remove: 'Supprimer la notification',
    range: '{from}–{to} sur {total}',
    prev: 'Page précédente',
    next: 'Page suivante',
    tabs: {
      all: 'Toutes',
      unread: 'Non lues',
      match_found: 'Correspondances',
      deadline_approaching: 'Échéances',
      procedure_update: 'Suivi des dossiers',
      system: 'Annonces',
    },
    empty: {
      allTitle: 'Aucune notification pour le moment',
      allMessage:
        "Quand la surveillance trouve une correspondance à risque élevé, qu'une échéance approche ou qu'un dossier que vous suivez change, cela apparaît ici.",
      unreadTitle: 'Tout est lu',
      unreadMessage: "Rien n'attend d'être lu.",
      kindTitle: 'Rien de ce type',
      kindMessage: 'Aucune notification de ce type ne vous a été envoyée.',
    },
  },

  prefs: {
    title: 'Préférences de notification',
    sub: "Pour chaque type d'événement : dans l'application, par e-mail, ou les deux.",
    event: 'Événement',
    channel: {
      in_app: "Dans l'application",
      email: 'Par e-mail',
    },
    kind: {
      match_found: {
        title: 'Correspondances à risque élevé',
        sub: 'Nouvelles correspondances à risque élevé sur les marques que vous voyez — un résumé par analyse, pas un par correspondance.',
      },
      deadline_approaching: {
        title: 'Échéances des dossiers',
        sub: 'Une semaine avant, la veille, le jour même et une fois dépassée — pour les dossiers qui vous sont attribués.',
      },
      procedure_update: {
        title: 'Suivi des dossiers',
        sub: 'Changements de statut, notes, documents et échéances des dossiers qui vous sont attribués — jamais vos propres actions.',
        viewer: 'Les mises à jour que le cabinet partage avec vous sur vos dossiers.',
      },
      system: {
        title: 'Annonces',
        sub: 'Avis concernant la plateforme elle-même, comme une maintenance prévue.',
      },
    },
    defaultNote: "Un interrupteur que vous n'avez jamais modifié est activé.",
    emailNote:
      "L'envoi d'e-mails en général est décidé pour toute la plateforme par son opérateur ; ces interrupteurs décident seulement si vous les recevez.",
    failed: "Votre réglage de notification n'a pas été enregistré",
    loadFailed: "Vos réglages de notification n'ont pas pu être chargés.",
  },

  reports: {
    title: "Rapports d'analyse",
    subtitle:
      'Un rapport pour chaque jour où la surveillance a analysé vos marques : ce qu’elle a comparé et ce qu’elle a trouvé.',
    dayCount: '{n} jour avec un rapport | {n} jours avec un rapport',
    newestFirst: 'Les plus récents en premier',
    loadFailed: "Les rapports n'ont pas pu être chargés.",
    emptyTitle: "Aucune analyse pour l'instant",
    emptyMessage:
      'Quand la surveillance analyse vos marques, chaque jour d’analyse aura ici son rapport.',
    col: {
      day: 'Jour',
      brands: 'Marques vérifiées',
      compared: 'Dépôts comparés',
      matches: 'Correspondances trouvées',
      high: 'Risque élevé',
      judged: 'Lues par le juge IA',
    },
    noScan: 'Aucune analyse enregistrée',
    open: 'Ouvrir le rapport du {day}',
    pdfFor: 'Télécharger le rapport du {day} en PDF',
    htmlFor: 'Télécharger le rapport du {day} en page imprimable',
    range: '{from}–{to} sur {total}',
    prev: 'Page précédente',
    next: 'Page suivante',
    note: "Les dépôts comparés sont les paires que le moteur a notées face à vos marques ; les correspondances sont celles qu'il a retenues. Un jour avec des correspondances mais sans analyse enregistrée date d'avant l'enregistrement des analyses.",
    pdf: 'Télécharger le PDF',
    html: 'Page imprimable',
    pdfUnavailable: 'Ce serveur ne peut pas produire de PDF pour le moment',
    pdfUnavailableSub:
      'La page imprimable a le même contenu : téléchargez-la et imprimez-la depuis votre navigateur.',
    pdfUnavailableCase:
      "L'opérateur de la plateforme peut activer la production de PDF sur le serveur.",
    downloadFailed: 'Le téléchargement a échoué',
    state: {
      new: 'Nouvelle',
      reviewed: 'Examinée',
      dismissed: 'Écartée',
      escalated: 'Dossier ouvert',
    },
    detail: {
      title: "Rapport d'analyse · {day}",
      noRun:
        "Aucune analyse n'a été enregistrée ce jour-là ; les correspondances ci-dessous l'ont été.",
      ran: 'Exécutée de {from} à {to} UTC',
      runs: '{n} analyse | {n} analyses',
      totals: 'La journée en chiffres',
      comparedHint: 'Paires candidates notées',
      judgedHint: 'Les autres ont été décidées par des règles de correspondance',
      matchesTitle: 'Correspondances trouvées',
      matchesSub: 'Toutes les correspondances enregistrées ce jour-là que vous pouvez voir.',
      noMatches: "Aucune correspondance n'a été enregistrée ce jour-là.",
      brandsTitle: 'Marques vérifiées',
      brandsSub:
        'Chaque marque comparée par les analyses du jour, celles au plus grand nombre de correspondances à risque élevé en premier.',
      noBrands: "Aucune marque n'a été analysée ce jour-là.",
      col: {
        trademark: 'Marque',
        found: 'Trouvée',
        registry: 'Registre',
        risk: 'Risque',
        decidedBy: 'Décidée par',
        state: 'Examen',
        deadline: "Délai d'opposition",
        client: 'Client',
        registries: 'Registres',
      },
      judge: 'Juge IA',
      rules: 'Règle de correspondance',
      showAll: 'Tout afficher ({n})',
      showFewer: 'Afficher moins',
      truncated: 'Liste des {shown} premières correspondances sur {total}.',
      back: 'Retour aux rapports',
      loadFailed: "Ce rapport n'a pas pu être chargé.",
    },
  },

  settings: {
    title: 'Paramètres',
    subtitle: 'Votre langue et vos notifications, et les connexions du cabinet aux registres.',
    subtitleViewer: 'Votre langue et vos notifications.',
    sections: 'Sections des paramètres',
    mainOnly: 'Seul le compte principal du cabinet peut modifier ceci.',
    nav: {
      language: 'Langue',
      notifications: 'Alertes',
      firm: 'Cabinet',
      registries: 'Registres nationaux',
      people: 'Personnes',
      later: "Pas encore dans l'application",
    },
    language: {
      title: 'Langue',
      sub: "La langue de l'application, et celle des e-mails et notifications qui vous sont envoyés.",
      emails: 'Les e-mails et notifications vous sont écrits en {language}.',
      mismatch: 'Vos e-mails sont encore écrits en {stored}.',
      useCurrent: 'Les écrire aussi en {language}',
      failed:
        "La langue a changé ici mais n'a pas été enregistrée sur votre compte : les e-mails gardent leur langue.",
    },
    firm: {
      title: 'Cabinet',
      sub: 'Votre cabinet tel qu’il apparaît sur les rapports imprimés.',
      name: 'Nom du cabinet',
      email: 'E-mail de contact',
      save: 'Enregistrer le cabinet',
      saved: 'Informations du cabinet enregistrées',
      failed: "Les informations du cabinet n'ont pas été enregistrées",
      nameRequired: 'Saisissez le nom du cabinet.',
      loadFailed: "Les informations du cabinet n'ont pas pu être chargées.",
    },
    registries: {
      title: 'Registres nationaux',
      sub: "Comment la plateforme atteint l'office des marques d'un pays, pour la surveillance nationale.",
      credentials:
        "Les mots de passe et les clés ne sont jamais saisis ici. L'opérateur de la plateforme les configure sur le serveur.",
      add: 'Ajouter un registre',
      country: 'Code pays',
      countryHint: 'Deux lettres, par exemple BG.',
      type: 'Mode de connexion',
      kind: {
        api: 'API web',
        ftp: 'Serveur FTP',
      },
      address: 'Adresse',
      addressHint:
        "L'adresse du serveur ou l'adresse web du flux du registre — sans nom d'utilisateur ni mot de passe.",
      useIt: 'Utiliser cette connexion',
      save: 'Enregistrer la connexion',
      badCountry: 'Saisissez un code pays de deux lettres.',
      exists: 'Ce pays est déjà connecté ; modifiez-le plutôt.',
      saved: 'Connexion {country} enregistrée',
      failed: "La connexion n'a pas été enregistrée",
      remove: 'Retirer',
      removeTitle: 'Retirer la connexion {country} ?',
      removeBody:
        "La plateforme cesse de l'utiliser. Les identifiants détenus par l'opérateur de la plateforme ne sont pas touchés.",
      removed: 'Connexion retirée',
      loadFailed: "Les connexions aux registres n'ont pas pu être chargées.",
      empty: 'Aucun registre national connecté',
      emptyMessage:
        "La surveillance nationale suit l'office des marques d'un pays. Une connexion indique comment l'atteindre.",
      active: 'Utilisée',
      paused: 'Non utilisée',
      credOnFile: "Identifiants : configurés par l'opérateur de la plateforme.",
      credMissing:
        "Identifiants : pas encore configurés — adressez-vous à l'opérateur de la plateforme.",
      adapterYes: 'La plateforme sait lire ce registre.',
      adapterNo:
        "La plateforme ne sait pas encore lire ce registre. La connexion est enregistrée ; aucune analyse nationale n'a lieu tant que ce n'est pas le cas.",
      other: 'Également enregistré : {keys}',
      updated: 'Mis à jour le {date}',
    },
    people: {
      title: 'Personnes',
      sub: 'Les collègues et les personnes chez les clients qui peuvent se connecter, et ce que chacun peut voir.',
      open: 'Gérer les personnes',
      mainOnly:
        'Seul le compte principal du cabinet ajoute des personnes et choisit ce qu’elles voient.',
    },
    later: {
      title: "Pas encore dans l'application",
      sub: 'Souvent demandé, et pas encore possible depuis cette page.',
      signinTitle: 'Sécurité de connexion',
      signin:
        "La connexion à deux facteurs et l'authentification unique ne sont pas encore disponibles.",
      matchingTitle: 'Comment les conflits sont notés',
      matching:
        'Le moteur de correspondance est réglé pour toute la plateforme par son opérateur, pas par cabinet.',
    },
  },

  /* Dossiers d'opposition (W10). Les codes des registres (EUIPO, WIPO) sont
   * imprimés depuis les données : ce sont des noms, ils ne se traduisent pas. */
  opp: {
    title: "Dossiers d'opposition",
    caseRef: 'Dossier {ref}',
    registryBg: 'Office bulgare des brevets',
    status: {
      under_review: "À l'étude",
      drafting: 'Rédaction',
      filed: 'Opposition déposée',
      awaiting_response: 'En attente de réponse',
      negotiation: 'Négociation',
      resolved: 'Résolu',
      closed: 'Clôturé',
    },
    outcome: {
      won: 'Gagné',
      settled: 'Transaction',
      lost: 'Perdu',
      withdrawn: 'Retiré',
    },
    priority: {
      urgent: 'Urgente',
      high: 'Haute',
      medium: 'Moyenne',
      low: 'Basse',
    },
    risk: {
      high: 'Risque élevé',
      med: 'Risque moyen',
      low: 'Risque faible',
      none: "Aucune correspondance derrière ce dossier, donc pas d'évaluation du risque",
    },
    verdict: {
      conflict: 'Conflit',
      strong_match: 'Conflit',
      unclear: 'Décision humaine nécessaire',
      review: 'Décision humaine nécessaire',
      no_conflict: 'Pas de conflit',
      dismiss: 'Pas de conflit',
    },
    kind: {
      statutory: 'Légal',
      internal: 'Interne',
    },
    noteCategory: {
      legal: 'Juridique',
      internal: 'Interne',
      status: 'Changement de statut',
    },
    docCategory: {
      filing: 'Dépôt',
      evidence: 'Preuve',
      correspondence: 'Correspondance',
      decision: 'Décision',
      agreement: 'Accord',
      other: 'Autre',
    },
    deadline: {
      provisional: "Provisoire : règle en cours d'examen",
      provisionalShort: 'provisoire',
      provisionalHelp:
        "Cette date suit une règle du registre que les avocats du cabinet n'ont pas encore confirmée. Vérifiez-la avant de vous y fier.",
      none: 'Aucun délai ouvert',
      finished: 'Dossier terminé',
      done: 'Fait le {date}',
      fromMark: 'Repris de la publication du registre',
      fromRule: 'Fixé par une règle de délai',
      fromFirm: 'Ajouté par le cabinet',
    },
    list: {
      subtitle:
        "Chaque conflit pour lequel le cabinet a ouvert un dossier : son statut, son prochain délai et qui s'en occupe.",
      filters: 'Filtres',
      create: "Créer un dossier d'opposition",
      cases: '{n} dossier | {n} dossiers',
      filtersOn: '{n} filtre actif | {n} filtres actifs',
      search: 'Rechercher un titre, un numéro, une marque ou un client…',
      searchLabel: 'Rechercher des dossiers',
      range: '{from}–{to} sur {total}',
      prev: 'Page précédente',
      next: 'Page suivante',
      emptyTitle: "Aucun dossier d'opposition pour l'instant",
      emptyMessage:
        'Un dossier est ouvert depuis une correspondance de la page de surveillance, ou manuellement depuis cette page.',
      toMonitoring: 'Aller à la surveillance',
      noMatchTitle: 'Aucun dossier ne correspond à ces filtres',
      noMatchMessage: 'Effacez les filtres pour voir tous les dossiers.',
      clearFilters: 'Effacer les filtres',
      loadFailed: "Les dossiers n'ont pas pu être chargés.",
    },
    kpi: {
      active: 'Dossiers actifs',
      openedThisMonth: '{n} ouverts ce mois-ci',
      urgent: 'En retard ou à échéance sous 14 jours',
      overdue: '{n} en retard',
      due14: '{n} à échéance sous 14 jours',
      awaiting: 'En attente de réponse',
      awaitingHint: "Déposés, en attente de l'autre partie",
      won: 'Gagnés ou transigés cette année',
      wonHint: 'sur {n} dossiers résolus cette année',
    },
    filter: {
      registry: 'Registre',
      status: 'Statut',
      deadline: 'Prochain délai',
      deadlineNote: 'Compté sur les dossiers actifs.',
      overdue: 'En retard',
      within: 'Sous {n} jours',
      any: "N'importe quelle date",
      priority: 'Priorité',
      activeOnly: 'Dossiers actifs uniquement',
      client: 'Client : {name}',
      brand: 'Marque : {name}',
      search: 'Recherche : {q}',
    },
    sort: {
      label: 'Ordre de tri',
      deadline: "Délai le plus proche d'abord",
      recent: 'Mis à jour récemment',
      priority: "Priorité la plus haute d'abord",
      created: "Dossiers les plus récents d'abord",
    },
    col: {
      conflict: 'Conflit de marques',
      risk: 'Risque de la correspondance',
      status: 'Statut',
      deadline: 'Prochain délai',
      lawyer: 'Avocat',
    },
    preview: {
      label: 'Aperçu du dossier',
      show: "Afficher dans l'aperçu",
      close: "Fermer l'aperçu",
      origin: "D'où vient ce dossier",
      judged: 'Lu par le juge IA',
      rules: 'Signalé par une règle de correspondance',
      byHand:
        "Ouvert manuellement. Aucune correspondance ne le sous-tend, il n'a donc pas d'évaluation du risque.",
      viewMatch: 'Voir comment la correspondance a été trouvée',
      nextDeadline: 'Prochain délai',
      open: 'Ouvrir le dossier',
    },
    fact: {
      lawyer: 'Avocat responsable',
      unassigned: 'Non attribué',
      client: 'Client concerné',
      documents: 'Pièces',
      files: '{n} fichier | {n} fichiers',
      notes: 'Notes du dossier',
      noteCount: '{n} note | {n} notes',
      lastActivity: 'Dernière activité',
      grounds: 'Motifs',
      notRecorded: 'Pas encore renseignés',
      caseNumber: 'Numéro du dossier',
      priority: 'Priorité',
      registry: 'Registre',
      filed: 'Date de dépôt',
      createdBy: 'Ouvert par',
      internalNotes: 'Notes de travail',
    },
    activity: {
      title: 'Activité juridique récente',
      sub: 'Les derniers changements sur tous les dossiers que vous pouvez voir.',
      empty: "Rien ne s'est encore passé sur aucun dossier.",
    },
    event: {
      created: 'Dossier ouvert manuellement',
      createdFromMatch: 'Dossier ouvert depuis une correspondance de la surveillance',
      status: 'Statut modifié de « {from} » à « {to} »',
      statusOutcome: 'Statut modifié de « {from} » à « {to} » : {outcome}',
      note: 'Note ajoutée ({category})',
      documentAdded: 'Pièce ajoutée : {name}',
      documentRemoved: 'Pièce supprimée : {name}',
      documentUpdated: 'Informations de la pièce modifiées : {name}',
      assigned: 'Attribué à {name}',
      assignedSomeone: "L'avocat responsable a changé",
      unassigned: "L'avocat responsable a été retiré",
      deadlineAdded: 'Délai ajouté : {title}, échéance le {date}',
      deadlineDone: 'Délai accompli : {title}',
      deadlineReopened: 'Délai rouvert : {title}',
      deadlineEdited: 'Délai modifié : {title}',
      deadlineRemoved: 'Délai supprimé : {title}',
      priority: 'Priorité modifiée de « {from} » à « {to} »',
      edited: 'Informations du dossier modifiées',
      windowClosed:
        "Le délai d'opposition contre cette marque a expiré le {date}, avant l'ouverture du dossier",
      other: 'Dossier mis à jour',
      someone: "Quelqu'un",
      clientVisible: 'Visible par le client',
    },
    create: {
      crumb: 'Nouveau dossier',
      title: "Ouvrir un dossier d'opposition",
      subtitle: 'Un dossier porte sur un seul conflit : la marque de votre client contre un dépôt.',
      submit: 'Ouvrir le dossier',
      submitting: 'Ouverture…',
      draftFailed:
        "Cette correspondance n'a pas pu être chargée. Elle a peut-être été supprimée, ou vous n'y avez pas accès.",
      existingTitle: 'Cette correspondance a déjà un dossier ouvert',
      existingBody:
        "Une correspondance ne peut avoir qu'un dossier ouvert à la fois. Poursuivez dans celui qui existe.",
      openExisting: 'Ouvrir le dossier existant',
      conflictSection: 'Le conflit',
      conflictFromMatch:
        'Repris de la correspondance. Ce qui identifie le conflit ne peut pas être modifié ici.',
      analysisSection: "Ce que l'outil de correspondance a trouvé",
      judgeScore: 'Probabilité de confusion selon le juge IA',
      ruleScore: "Similarité issue d'une règle de correspondance, pas une probabilité",
      noJustification: "Une règle a signalé cette paire ; aucun modèle n'a rédigé de motif.",
      pickSection: 'La marque de votre client',
      client: 'Client concerné',
      clientSearch: 'Rechercher des clients…',
      chooseClient: 'Choisir un client',
      brand: 'Marque',
      brandSearch: 'Rechercher parmi les marques de ce client…',
      chooseBrand: 'Choisir une marque',
      noBrands: "Ce client n'a pas encore de marque.",
      narrow: '{shown} affichés sur {total}. Tapez pour affiner la liste.',
      opposedSection: 'La marque contestée',
      markName: 'Nom de la marque',
      markNumber: 'Numéro de demande',
      markOwner: 'Déposant ou titulaire',
      registry: 'Registre',
      registryNone: 'Non renseigné',
      handNote:
        "Aucune correspondance ne sous-tend un dossier ouvert manuellement : il n'a ni évaluation du risque ni délai issu d'un registre. Ajoutez le délai sur la page du dossier.",
      caseSection: 'Le dossier',
      caseTitle: 'Titre du dossier',
      caseTitleHint: "S'il reste vide, le dossier s'appellera « {title} ».",
      caseNumber: 'Numéro du dossier',
      caseNumberHint: "La référence de l'office, dès qu'il y en a une.",
      priority: 'Priorité',
      startAs: 'Statut de départ',
      lawyer: 'Avocat responsable',
      grounds: 'Motifs',
      groundsPlaceholder: 'p. ex. art. 8, par. 1, point b), RMUE',
      notes: 'Notes internes',
      notesHint: 'Les notes de travail du cabinet. Jamais montrées au client.',
      markDeadline:
        "Le délai d'opposition de la marque trouvée, le {date}, devient le premier délai du dossier.",
      markDeadlinePassed:
        "Le délai d'opposition contre cette marque a expiré le {date}. Le dossier le consigne au lieu de porter un délai dépassé.",
      afterNote:
        'Les pièces, délais et notes sont ajoutés sur la page du dossier une fois celui-ci ouvert.',
      pickBrand: 'Choisissez la marque de votre client.',
      nameMark: 'Indiquez la marque contestée.',
      failed: "Le dossier n'a pas été ouvert",
      created: 'Dossier ouvert',
      lawyersFailed:
        "La liste des avocats n'a pas pu être chargée. Le dossier pourra être attribué plus tard.",
    },
    detail: {
      assigned: '{name} responsable',
      filed: 'Déposée le {date}',
      backToCases: 'Retour aux dossiers',
      addNote: 'Ajouter une note',
      upload: 'Téléverser une pièce',
      uploading: 'Téléversement…',
      updateStatus: 'Changer le statut',
      delete: 'Supprimer le dossier',
      deleteTitle: 'Supprimer ce dossier ?',
      deleteBody:
        "Le dossier, son historique, ses notes et ses pièces sont supprimés définitivement. Pour arrêter le travail tout en gardant l'historique, clôturez-le plutôt.",
      deleted: 'Dossier supprimé',
      deleteFailed: "Le dossier n'a pas été supprimé",
      readOnly: 'Vous pouvez consulter ce dossier mais pas le modifier.',
      sections: 'Sections du dossier',
      tabConflict: 'Conflit',
      tabAnalysis: 'Analyse',
      tabNotes: 'Notes du dossier',
      tabDocs: 'Pièces',
      tabTimeline: 'Historique',
      protected: 'La marque de votre client',
      opposed: 'Marque contestée',
      yourClient: 'Votre client',
      opposedTag: 'Contestée',
      applicationNo: 'Numéro de demande',
      registered: 'Enregistrée',
      filedOn: 'Déposée',
      classes: 'Classes de Nice',
      type: 'Type de marque',
      owner: 'Titulaire',
      windowCloses: "Délai d'opposition",
      handTyped: "Saisi manuellement : seul ce que l'avocat a saisi est connu.",
      analysisJudge: 'Lu par le juge IA. Son verdict et ses propres mots figurent ci-dessous.',
      analysisRules:
        "Signalé par une règle de correspondance. Aucun modèle n'a lu cette paire, et le chiffre est une similarité, pas une probabilité.",
      score: '{n} sur 100',
      verdict: 'Verdict du juge',
      overlap: 'Classes de Nice communes',
      noOverlap: 'Aucune classe de Nice en commun',
      onlyTheirs: 'la leur seulement',
      loadFailed: "Une partie de ce dossier n'a pas pu être chargée.",
      editDetails: 'Modifier',
      saveDetails: 'Enregistrer',
      detailsSaved: 'Informations du dossier enregistrées',
      detailsFailed: "Les informations du dossier n'ont pas été enregistrées",
      summaryPdf: 'Résumé du dossier (PDF)',
    },
    notes: {
      title: 'Notes du dossier',
      sub: 'Constats juridiques, notes de travail internes et changements de statut.',
      all: 'Toutes',
      placeholder: "Rédigez une note juridique, un constat ou une consigne pour l'équipe…",
      visible: 'Visible par le client',
      visibleHint: 'Le compte client en lecture verra cette note.',
      post: 'Publier la note',
      posting: 'Publication…',
      empty: 'Aucune note pour le moment.',
      emptyCategory: 'Aucune note dans cette catégorie.',
      added: 'Note ajoutée',
      failed: "La note n'a pas été enregistrée",
    },
    docs: {
      title: 'Pièces du dossier',
      sub: 'Dépôts, preuves, correspondance et décisions.',
      category: 'Catégorie',
      choose: 'Téléverser un fichier',
      hint: 'PDF, Word, texte brut ou image.',
      visibleShort: 'Visible par le client',
      hiddenShort: 'Cabinet uniquement',
      showToClient: 'Montrer au client',
      hideFromClient: 'Masquer au client',
      download: 'Télécharger {name}',
      delete: 'Supprimer {name}',
      deleteTitle: 'Supprimer {name} ?',
      deleteBody: 'Le fichier est retiré de ce dossier. Cette action est irréversible.',
      empty: 'Aucune pièce dans ce dossier.',
      uploaded: '{name} téléversé',
      failed: 'Le téléversement a échoué',
      actionFailed: "Cette modification de la pièce n'a pas été enregistrée",
    },
    timeline: {
      title: 'Historique du dossier',
      sub: "Chaque modification du dossier, la plus récente d'abord. L'historique ne peut pas être modifié.",
      empty: 'Rien d’enregistré pour le moment.',
    },
    lifecycle: {
      title: 'Statut du dossier',
      moveTo: 'Passer à',
      choose: "Choisir l'étape suivante",
      outcome: "Comment s'est-il terminé ?",
      chooseOutcome: 'Choisir une issue',
      reason: 'Pourquoi le dossier est-il clôturé ?',
      reopenReason: 'Pourquoi est-il rouvert ? (facultatif)',
      filingDate: 'Date de dépôt',
      clientVisible: 'Montrer cette étape au client',
      apply: 'Changer le statut',
      applying: 'Enregistrement…',
      needOutcome: "Indiquez l'issue du dossier.",
      needReason: 'Indiquez pourquoi le dossier est clôturé.',
      changed: 'Statut passé à « {status} »',
      failed: "Le statut n'a pas été modifié",
      noMoves: "Aucune étape suivante à partir d'ici.",
      outcomeLine: 'Issue : {outcome}',
    },
    deadlines: {
      title: 'Délais',
      empty: 'Aucun délai dans ce dossier.',
      markDone: 'Marquer comme fait',
      reopen: 'Rouvrir',
      delete: 'Supprimer le délai',
      deleteTitle: 'Supprimer « {title} » ?',
      deleteBody: "Le délai est supprimé. L'historique garde la trace de son existence.",
      add: 'Ajouter un délai',
      adding: 'Ajout…',
      newTitle: 'Ce qui est dû',
      newDate: "Date d'échéance",
      newKind: 'Nature',
      newNote: 'Note (facultatif)',
      failed: "Le délai n'a pas été enregistré",
    },
    related: {
      title: "Dossiers d'opposition",
      count: '{n} dossier | {n} dossiers',
      none: "Aucun dossier d'opposition pour l'instant.",
      viewAll: 'Tout voir',
      create: 'Nouvelle opposition',
      failed: "Les dossiers n'ont pas pu être chargés.",
    },
    match: {
      openCase: "Ouvrir un dossier d'opposition",
      openCaseHint:
        "Ouvre un dossier prérempli à partir de cette correspondance. Une correspondance ne peut avoir qu'un dossier ouvert à la fois.",
    },
  },

  clients: {
    title: 'Clients et titulaires de marques',
    subtitle:
      'Les sociétés dont le cabinet protège les marques : leurs marques, leur surveillance et leurs dossiers.',
    filters: 'Filtres',
    add: 'Ajouter un client',
    unit: 'clients',
    shown: '{n} sur {total} clients',
    filtersActive: '{n} filtre actif | {n} filtres actifs',
    searchPlaceholder: 'Rechercher des clients…',
    unassigned: 'Non attribué',
    kpi: {
      label: 'Aperçu des clients',
      total: 'Clients',
      newThisQuarter: 'nouveaux ce trimestre',
      trademarks: 'Marques',
      perClient: '{n} par client en moyenne',
      highRisk: 'Clients à risque élevé',
      highRiskHint: 'Deux dossiers ouverts ou plus',
      registries: 'Registres',
      registriesHint: 'Où leurs marques sont surveillées',
    },
    filter: {
      name: 'Nom du client',
      namePlaceholder: 'Société ou contact',
      registry: 'Registre',
      monitoring: 'Surveillance',
      risk: 'Niveau de risque',
    },
    chip: {
      risk: 'Risque : {level}',
      search: 'Recherche : {q}',
    },
    risk: {
      high: 'Élevé',
      med: 'Moyen',
      low: 'Faible',
      none: 'Non évalué',
    },
    monitoring: {
      full: 'Entièrement surveillé',
      partial: 'Partiellement surveillé',
      paused: 'Surveillance suspendue',
      none: 'Non surveillé',
      unknown: 'Surveillance inconnue',
    },
    sort: {
      label: 'Trier les clients',
      name: 'Nom, de A à Z',
      nameDesc: 'Nom, de Z à A',
      trademarks: 'Le plus de marques',
      cases: 'Le plus de dossiers ouverts',
      recent: 'Modifiés récemment',
    },
    empty: {
      title: 'Aucun client ne correspond à votre recherche',
      message: 'Essayez une autre recherche ou effacez les filtres actifs.',
      reset: 'Réinitialiser les filtres',
    },
    col: {
      client: 'Client',
      registries: 'Registres',
      risk: 'Risque',
      lawyer: 'Avocat',
      trademarks: 'Marques',
    },
    preview: {
      label: 'Aperçu du client',
      close: "Fermer l'aperçu",
      summary: 'Résumé',
      registries: '{n} registre | {n} registres',
      monitoring: 'Surveillées',
      openCases: 'Dossiers ouverts',
      allMarks: 'Toutes les {n}',
      noMarks: 'Aucune marque au dossier.',
      registered: 'Enregistrée',
      pending: 'En cours',
      classes: 'Classe {list} | Classes {list}',
      lawyers: 'Avocats',
      activity: 'Activité récente des dossiers',
      noActivity: "Pas encore d'activité dans les dossiers.",
      open: 'Ouvrir le dossier du client',
    },
    activity: {
      title: 'Activité récente des dossiers',
      sub: "Les dernières lignes de l'historique de chaque dossier, tous clients confondus.",
      empty: "Pas encore d'activité dans les dossiers.",
    },
    quick: {
      title: 'Actions rapides',
      addClient: 'Ajouter une société dont le cabinet protège les marques.',
      addTrademark: 'Ajouter une marque',
      addTrademarkSub:
        "Ajouter une marque au portefeuille d'un client et choisir où la surveiller.",
      monitoring: 'Ouvrir la surveillance',
      monitoringSub: 'Examiner ce que la surveillance a trouvé contre les marques de vos clients.',
      case: 'Ouvrir un dossier',
      caseSub: "Ouvrir un dossier d'opposition pour un client.",
    },
  },

  client: {
    back: 'Retour aux clients',
    edit: 'Modifier le client',
    eyebrow: 'Client · {id}',
    riskClient: 'Risque : {level}',
    trademarkCount: '{n} marque | {n} marques',
    openCaseCount: '{n} dossier ouvert | {n} dossiers ouverts',
    contact: 'Contact : {name}',
    monitoring: 'Surveillance',
    watchedOf: '{n} marques sur {total} surveillées',
    openMonitoring: 'Ouvrir la surveillance',
    newCase: 'Nouveau dossier',
    sections: 'Sections du dossier client',
    failed: 'Impossible de charger cette partie. Rechargez la page pour réessayer.',
    notWatched: 'Non surveillée',
    classes: 'Classe {list} | Classes {list}',
    registeredOn: 'Enregistrée le {date}',
    kpi: {
      label: 'Aperçu du client',
      coverage: 'Couverture de la surveillance',
      casesOpen: 'Des dossiers ouverts attendent une réponse',
      casesNone: 'Aucun dossier ouvert',
    },
    tab: {
      portfolio: 'Portefeuille',
      cases: 'Dossiers',
      people: 'Personnes',
      matches: 'Correspondances',
      activity: 'Activité',
      monitoring: 'Surveillance',
      documents: 'Documents',
    },
    portfolio: {
      title: 'Portefeuille de marques',
      sub: 'Toutes les marques du client au dossier, enregistrées ou en cours.',
      all: 'Tout voir dans Mes marques',
      emptyTitle: 'Pas encore de marques',
      empty: "Ce client n'a aucune marque au dossier.",
    },
    people: {
      title: 'Personnes ayant accès',
      sub: 'Les avocats qui travaillent pour ce client et les utilisateurs du client lui-même. Les comptes principaux du cabinet voient tous les clients et ne sont pas listés.',
      empty: "Personne n'a encore reçu l'accès à ce client.",
      add: 'Donner accès',
      manager: 'Avocat',
      viewer: 'Utilisateur du client',
      off: 'Désactivé',
      wholeCompany: 'Toute la société',
      someMarks: '{n} marque | {n} marques',
    },
    matches: {
      title: 'Correspondances ouvertes',
      sub: "Ce que la surveillance a trouvé contre les marques de ce client, les délais les plus proches d'abord.",
      lastScan: 'Dernière analyse le {date}',
      awaiting: 'en attente d’examen',
      high: 'à risque élevé',
      closingSoon: 'à risque élevé dont le délai expire sous 14 jours',
      stillOpen: 'dont le délai est encore ouvert',
      none: "Aucune correspondance dont le délai d'opposition est encore ouvert.",
      against: 'contre {brand}',
    },
    activity: {
      title: 'Activité des dossiers',
      sub: "Les dernières lignes des dossiers d'opposition de ce client.",
      empty: "Pas encore d'activité dans les dossiers de ce client.",
    },
    coverage: {
      title: 'Surveillance par registre',
      sub: 'Combien de marques de ce client sont surveillées sur chaque registre.',
      ofMarks: 'sur {n} marque | sur {n} marques',
      lastScan: 'Dernière analyse le {date}',
      waiting: 'En attente de sa première analyse',
      notWatched: 'Aucune surveillée ici',
    },
    docs: {
      title: 'Documents',
      sub: 'Les fichiers des marques de ce client. On les ajoute sur la page de la marque.',
      empty: 'Aucun document sur les marques de ce client.',
      open: 'Ouvrir {name}',
      openFailed: "Impossible d'ouvrir le fichier",
      kind: {
        logo: 'Logo',
        certificate: 'Certificat',
        evidence: 'Preuve',
        correspondence: 'Correspondance',
        other: 'Autre',
      },
    },
  },

  /* The pages translated in the Wave 3 pass (2026-09-30), one file per area
   * so they could be written side by side. */
  ...monitoring,
  ...trademarks,
  ...general,
  ...people,
  ...explain,
} as const
